<template>
  <div class="container py-2 operator-view">
    <div class="hero mb-2 p-2 p-md-3 d-flex align-items-center justify-content-between">
      <div>
        <h2 class="mb-1 text-white">Operator</h2>
        <p class="mb-0 text-white-50">{{ heroSubtitle }}</p>
      </div>
      <span v-if="loading" class="mdi mdi-loading mdi-spin text-white display-4"></span>
    </div>

    <div v-if="errors.length" class="alert alert-danger" role="alert">
      <ul class="mb-0">
        <li v-for="(e, idx) in errors" :key="idx">{{ e }}</li>
      </ul>
    </div>

    <div class="row operator-grid compact-gutters">
      <div class="col-lg-8 col-md-12 operator-left-col">
    <div class="row">
      <div class="col-md-6 mb-2" v-if="canViewGates && gates.length">
        <div class="card shadow-lg border-0 rounded-lg animate-up">
          <div class="card-header gradient-header d-flex align-items-center text-white">
            <span class="mdi mdi-gate mr-2"></span>
            <strong>Gates</strong>
          </div>
          <div class="card-body">
            <label class="form-label mb-2 font-weight-bold" for="gateSelect">
              <span class="mdi mdi-chevron-down-circle-outline text-primary mr-1"></span>
              Select Gate
            </label>
            <select id="gateSelect" class="form-control fancy-select" v-model="selectedGate" @change="onGateChange" @focus="activateSelector('gate')" @mousedown="activateSelector('gate')">
              <option :value="null" disabled>Select a gate...</option>
              <option v-for="g in sortedGates" :key="g.key" :value="g.key">
                {{ g.name }}
              </option>
            </select>
            <small v-if="selectedGate" class="text-muted d-block mt-1 fade-in" style="font-size: 0.75rem;">
              Selected: <span class="badge badge-pill badge-primary">{{ selectedGate }}</span>
            </small>
          </div>
        </div>
      </div>

      <div class="col-md-6 mb-2" v-if="canViewClaims && claims.length">
        <div class="card shadow-lg border-0 rounded-lg animate-up" style="animation-delay: 80ms;">
          <div class="card-header gradient-header-2 d-flex align-items-center text-white">
            <span class="mdi mdi-briefcase mr-2"></span>
            <strong>Claims</strong>
          </div>
          <div class="card-body">
            <label class="form-label mb-2 font-weight-bold" for="claimSelect">
              <span class="mdi mdi-chevron-down-circle-outline text-success mr-1"></span>
              Select Claim
            </label>
            <select id="claimSelect" class="form-control fancy-select" v-model="selectedClaim" @change="onClaimChange" @focus="activateSelector('claim')" @mousedown="activateSelector('claim')">
              <option :value="null" disabled>Select a claim...</option>
              <option v-for="c in sortedClaims" :key="c.key" :value="c.key">
                {{ c.name }}
              </option>
            </select>
            <small v-if="selectedClaim" class="text-muted d-block mt-1 fade-in" style="font-size: 0.75rem;">
              Selected: <span class="badge badge-pill badge-success">{{ selectedClaim }}</span>
            </small>
          </div>
        </div>
      </div>
    </div>

    <div v-if="!loading && (!canViewGates || !gates.length) && (!canViewClaims || !claims.length) && (!gates.length && !claims.length)" class="text-muted">
      No operator data available for your account.
    </div>

    <div v-if="resultMode" class="mt-2 animate-in">
      <div class="card shadow-lg border-0 rounded-lg">
        <div class="card-header d-flex align-items-center justify-content-between gradient-header-soft text-white">
          <div>
            <strong class="mr-2">Results</strong>
            <small class="ml-2">Mode: <span class="badge badge-light text-uppercase">{{ resultMode }}</span></small>
          </div>
          <div class="d-flex align-items-center">
            <span class="badge badge-warning">Count: {{ resultCount }}</span>
          </div>
        </div>
        <div class="card-body p-0 results-wrapper">
          <div v-if="!result.length && !queryLoading" class="p-3 text-muted">No flights found for the selected {{ resultMode }}.</div>
          <div v-if="queryLoading || result.length" class="table-responsive results-scroll">
              <table class="table table-hover mb-0 pretty-table">
                <thead>
                  <tr>
                    <th :class="['sortable', {active: opSortKey==='flight'}]" @click="updateOperatorSort('flight')">
                      Flight
                      <span class="mdi sort-icon"
                            :class="opSortKey==='flight' ? (opSortDir==='asc' ? 'mdi-chevron-down' : 'mdi-chevron-up') : 'mdi-swap-vertical'">
                      </span>
                    </th>
                    <th :class="['sortable', {active: opSortKey==='airline'}]" @click="updateOperatorSort('airline')">
                      Airline
                      <span class="mdi sort-icon"
                            :class="opSortKey==='airline' ? (opSortDir==='asc' ? 'mdi-chevron-down' : 'mdi-chevron-up') : 'mdi-swap-vertical'">
                      </span>
                    </th>
                    <th :class="['sortable', {active: opSortKey==='type'}]" @click="updateOperatorSort('type')">
                      Type
                      <span class="mdi sort-icon"
                            :class="opSortKey==='type' ? (opSortDir==='asc' ? 'mdi-chevron-down' : 'mdi-chevron-up') : 'mdi-swap-vertical'">
                      </span>
                    </th>
                    <th :class="['sortable', {active: opSortKey==='status'}]" @click="updateOperatorSort('status')">
                      {{ resultMode==='claim' ? 'Bag Status' : 'Status' }}
                      <span class="mdi sort-icon"
                            :class="opSortKey==='status' ? (opSortDir==='asc' ? 'mdi-chevron-down' : 'mdi-chevron-up') : 'mdi-swap-vertical'">
                      </span>
                    </th>
                    <th :class="['sortable', {active: opSortKey==='city'}]" @click="updateOperatorSort('city')">
                      {{ resultMode==='claim' ? 'Arriving From' : 'Departing To' }}
                      <span class="mdi sort-icon"
                            :class="opSortKey==='city' ? (opSortDir==='asc' ? 'mdi-chevron-down' : 'mdi-chevron-up') : 'mdi-swap-vertical'">
                      </span>
                    </th>
                    <th :class="['sortable', {active: opSortKey==='time'}]" @click="updateOperatorSort('time')">
                      Flight Time
                      <span class="mdi sort-icon"
                            :class="opSortKey==='time' ? (opSortDir==='asc' ? 'mdi-chevron-down' : 'mdi-chevron-up') : 'mdi-swap-vertical'">
                      </span>
                    </th>
                    <th v-if="resultMode==='gate'" :class="['sortable', {active: opSortKey==='gate'}]" @click="updateOperatorSort('gate')">
                      Gate
                      <span class="mdi sort-icon"
                            :class="opSortKey==='gate' ? (opSortDir==='asc' ? 'mdi-chevron-down' : 'mdi-chevron-up') : 'mdi-swap-vertical'">
                      </span>
                    </th>
                    <th v-if="resultMode==='claim'" :class="['sortable', {active: opSortKey==='baggage'}]" @click="updateOperatorSort('baggage')">
                      Baggage
                      <span class="mdi sort-icon"
                            :class="opSortKey==='baggage' ? (opSortDir==='asc' ? 'mdi-chevron-down' : 'mdi-chevron-up') : 'mdi-swap-vertical'">
                      </span>
                    </th>
                  </tr>
                </thead>
                <PortalTableBody :loading="queryLoading" variant="operator">
                  <tr v-for="(row, idx) in resultSorted" :key="row.uid || row.flight_guid || (row.airline_iata_code + '-' + row.flight_number)"
                      :class="['selectable', selectedRowKey === rowKey(row, idx) ? 'selected' : '', (justAdded[rowKey(row, idx)] ? 'row-added' : '')]"
                      @click="selectRow(row, idx)">
                    <td>{{ row.airline_iata_code }} {{ row.flight_number }}</td>
                    <td>{{ row.airline_iata_code }}</td>
                    <td class="text-capitalize">{{ row.flight_type || '-' }}</td>
                    <td>{{ displayStatus(row, idx) }}</td>
                    <td>{{ row.arrival_airport_city || '-' }}</td>
                    <td>{{ row.estimated_flight_time || '-' }}</td>
                    <td v-if="resultMode==='gate'">{{ row.gate || '-' }}</td>
                    <td v-if="resultMode==='claim'">{{ row.baggage || '-' }}</td>
                  </tr>
                </PortalTableBody>
              </table>
            </div>
        </div>
      </div>
    </div>

      </div>
      <div class="col-lg-4 col-md-12 d-flex justify-content-center operator-right-col">
    <!-- Unified radial action control: visible for gate mode; disabled until a row is selected -->
    <div v-if="resultMode==='gate'" class="radial-footer animate-in">
      <div class="radial-wrapper gate-mode" :class="{ 'gate-engaged': selectedEnabled && selectedRowKey, refreshing: gateRefreshing }">
        <!-- Segments -->
        <button class="segment seg1" :class="radialClass('Boarding')" @click="actionOption('Boarding')" title="Boarding">
          <span class="seg-text">Boarding</span>
        </button>
        <button class="segment seg2" :class="radialClass('Gates Closed')" @click="actionOption('Gates Closed')" title="Gates Closed">
          <span class="seg-text">Gate Closed</span>
        </button>
        <button class="segment seg3" :class="radialClass('Last Call')" @click="actionOption('Last Call')" title="Last Call">
          <span class="seg-text">Final Call</span>
        </button>
        <button class="segment seg4" :class="radialClass('Cancelled')" @click="actionOption('Cancelled')" title="Cancel">
          <span class="seg-text">Cancel</span>
        </button>

        <!-- Center enable button with countdown -->
        <button class="center-btn" :class="{enabled: selectedEnabled, disabled: !selectedRowKey, unlocking: isUnlocking}" @click="toggleSelectedEnable" :disabled="!selectedRowKey">
          <div class="progress-ring" :style="progressStyle"></div>
          <span v-if="!selectedEnabled">
            <span v-if="!selectedRowKey" class="small">Select
              <br>row
            </span>
            <span v-else class="mdi mdi-lock-outline"></span>
          </span>
          <span v-else>
            <span class="mdi mdi-flash-outline pulse-bright mr-1"></span>{{ selectedRemaining }}s
          </span>
        </button>
        <!-- Light overlay spinner when refreshing gates -->
        <div v-if="gateRefreshing" class="refresh-overlay">
          <span class="mdi mdi-loading mdi-spin"></span>
        </div>
      </div>
      <div class="radial-caption mt-5 text-center">
        <template v-if="selectedRowKey">
          <strong>Selected:</strong> {{ selectedRowLabel }}
        </template>
        <template v-else>
          <span class="text-muted">Tap a flight row above to enable actions</span>
        </template>
      </div>
    </div>
    <!-- Radial action control: Claim mode (3 wedge options) -->
    <div v-if="resultMode==='claim'" class="radial-footer animate-in">
      <div class="radial-wrapper tri-mode" :class="{ refreshing: claimRefreshing, 'claim-engaged': selectedEnabled && selectedRowKey }">
        <!-- SVG-based 3 wedge sectors for precise clicks and labels -->
        <svg class="tri-svg" viewBox="0 0 100 100">
          <g class="sec s1" :class="radialClassClaim('First Belt')" @click="onTriClick('First Belt')">
            <!-- Donut sector: angles -60° to 60° -->
            <path d="M74,8.432 A48,48 0 0,1 74,91.568 L62,70.784 A24,24 0 0,0 62,29.216 Z" />
            <text x="79" y="50" class="tri-label" text-anchor="middle" dominant-baseline="middle">F B</text>
          </g>
          <g class="sec s2" :class="radialClassClaim('Last Belt')" @click="onTriClick('Last Belt')">
            <!-- Donut sector: angles 60° to 180° -->
            <path d="M74,91.568 A48,48 0 0,1 2,50 L26,50 A24,24 0 0,0 62,70.784 Z" />
            <text x="36" y="75" class="tri-label" text-anchor="middle" dominant-baseline="middle">L B</text>
          </g>
          <g class="sec s3" :class="radialClassClaim('Cancelled')" @click="onTriClick('Cancelled')">
            <!-- Donut sector: angles 180° to 300° -->
            <path d="M2,50 A48,48 0 0,1 74,8.432 L62,29.216 A24,24 0 0,0 26,50 Z" />
            <text x="36" y="25" class="tri-label" text-anchor="middle" dominant-baseline="middle">Cancel</text>
          </g>
        </svg>

        <!-- Center enable button with countdown -->
        <button class="center-btn" :class="{enabled: selectedEnabled, disabled: !selectedRowKey, unlocking: isUnlocking}" @click="toggleSelectedEnable" :disabled="!selectedRowKey">
          <div class="progress-ring" :style="progressStyle"></div>
          <span v-if="!selectedEnabled">
            <span v-if="!selectedRowKey" class="small">Select
              <br>row
            </span>
            <span v-else class="mdi mdi-lock-outline"></span>
          </span>
          <span v-else>
            <span class="mdi mdi-flash-outline pulse-bright mr-1"></span>{{ selectedRemaining }}s
          </span>
        </button>
      </div>
      <div class="radial-caption mt-2 text-center">
        <template v-if="selectedRowKey">
          <strong>Selected:</strong> {{ selectedRowLabel }}
        </template>
        <template v-else>
          <span class="text-muted">Tap a flight row above to enable actions</span>
        </template>
      </div>
      <!-- Claim timestamps pills (always visible in claim mode) -->
      <div class="claim-pills text-center mt-2 animate-in">
        <span class="pill pill-first mr-2">
          <span class="label">First Bag</span>
          <span class="value">{{ formatTs(selectedClaimTimes.firstBag) }}</span>
        </span>
        <span class="pill pill-last">
          <span class="label">Last Bag</span>
          <span class="value">{{ formatTs(selectedClaimTimes.lastBag) }}</span>
        </span>
      </div>
    </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import moment from 'moment';
import { decryptData } from '../js/utils/encrypt';
import PortalTableBody from '../components/PortalTableBody.vue';

export default {
  name: 'OperatorView',
  components: {
    PortalTableBody,
  },
  data() {
    return {
      gates: [],
      claims: [],
      loading: false,
      errors: [],
      permissions: [],
      canViewGates: false,
      canViewClaims: false,
      // Which selector is currently active/enabled: 'gate' | 'claim'
      currentSelector: 'gate',
      selectedGate: null,
      selectedClaim: null,
      queryLoading: false,
      resultMode: null,
      resultCount: 0,
      serverCount: 0,
      result: [],
      resultSortCache: {},
      // Per-row action state keyed by stable row key
      rowActions: {},
      actionWindowSeconds: parseInt(process.env.VUE_APP_OPERATOR_ACTION_WINDOW_SECONDS, 10) || 30,
      selectedRowKey: null,
      selectedRowIndex: null,
      selectedRowData: null,
      // Baggage master data fetched when viewing claim results
      baggageAll: [],
      // shows a brief refresh animation on claim radial while reloading
      claimRefreshing: false,
      // shows a brief refresh animation on gate radial while reloading
      gateRefreshing: false,
      // Local store of per-row claim timestamps { [rowKey]: { firstBag: ts, lastBag: ts } }
      claimTimes: {},
      // Time window controls (mins) from env
      rollInClaimMin: parseInt(process.env.VUE_APP_ROLLIN_CLAIM_MIN, 10) || 0,
      rollOutClaimMin: parseInt(process.env.VUE_APP_ROLLOUT_CLAIM_MIN, 10) || 0,
      rollInGateMin: parseInt(process.env.VUE_APP_ROLLIN_GATE_MIN, 10) || 0,
      rollOutGateMin: parseInt(process.env.VUE_APP_ROLLOUT_GATE_MIN, 10) || 0,
      treatZuluAsLocalTime: String(process.env.VUE_APP_TREAT_ZULU_AS_LOCAL_TIME || '').toLowerCase() === 'true',
      // Polling interval (sec) from env
      flightDetailsPollSec: parseInt(process.env.VUE_APP_FLIGHT_DETAILS_POLL_SEC, 10) || 0,
      pollTimer: null,
      // Track unlock animation state
      isUnlocking: false,
      // Operator table sorting (default: Flight Time ascending)
      opSortKey: 'time', // one of: flight, airline, type, status, city, time, gate, baggage
      opSortDir: 'asc', // 'asc' | 'desc'
      opSortInited: false,
      // Row tracking for add-only animations
      knownKeys: {},
      justAdded: {},
    };
  },
  beforeDestroy() {
    // Clear any running timers to avoid leaks
    if (this.rowActions) {
      Object.values(this.rowActions).forEach(st => {
        if (st && st.timer) {
          clearInterval(st.timer);
          st.timer = null;
        }
      });
    }
    // Ensure background polling is stopped when leaving the page
    this.stopPolling();
  },
  // If this component is used inside <keep-alive>, ensure we stop polling when deactivated
  deactivated() {
    this.stopPolling();
  },
  // Optionally restart polling when returning, startPolling() is safe and will no-op without a valid mode/value
  activated() {
    this.startPolling();
  },
  created() {
    // Load persisted claim times
    try {
      const raw = localStorage.getItem('claimTimesV1');
      if (raw) this.claimTimes = JSON.parse(raw) || {};
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('Failed to load claimTimesV1');
      this.claimTimes = {};
    }
    // Prune any timestamps older than today
    this.pruneClaimTimes();

    // Defensive: ensure header sort handler exists even under partial HMR updates
    if (typeof this.updateOperatorSort !== 'function') {
      // eslint-disable-next-line no-console
      console.warn('[OPERATOR] updateOperatorSort missing at created(); installing fallback');
      this.updateOperatorSort = (key) => {
        if (this.opSortKey === key) {
          this.opSortDir = this.opSortDir === 'asc' ? 'desc' : 'asc';
        } else {
          this.opSortKey = key;
          this.opSortDir = 'asc';
        }
      };
    }
  },
  computed: {
    sortedGates() {
      return [...this.gates].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
    },
    sortedClaims() {
      return [...this.claims].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
    },
    heroSubtitle() {
      const g = !!this.canViewGates;
      const c = !!this.canViewClaims;
      if (g && c) return 'Select a Gate or Claim to view live flight data';
      if (g) return 'Select a Gate to view live flight data';
      if (c) return 'Select a Claim to view live flight data';
      return 'No operator data available for your account';
    },
    selectedState() {
      return this.getSelectedState();
    },
    // Sorted copy of operator results for table rendering
    resultSorted() {
      const arr = Array.isArray(this.result) ? [...this.result] : [];
      const key = this.opSortKey;
      const dir = this.opSortDir === 'desc' ? -1 : 1;
      const cache = this.resultSortCache;
      const safeStr = v => (v == null ? '' : String(v));
      const cmpStr = (a, b) => safeStr(a).toLowerCase().localeCompare(safeStr(b).toLowerCase());
      const cmpDate = (a, b) => {
        const ta = Date.parse(a);
        const tb = Date.parse(b);
        const va = Number.isFinite(ta) ? ta : Number.POSITIVE_INFINITY;
        const vb = Number.isFinite(tb) ? tb : Number.POSITIVE_INFINITY;
        return va - vb;
      };
      const getVal = (row, idx) => {
        const rowCache = cache[this.rowKey(row, idx)];
        if (rowCache && rowCache[key] !== undefined) {
          return rowCache[key];
        }
        switch (key) {
          case 'flight':
            return `${safeStr(row.airline_iata_code)} ${safeStr(row.flight_number)}`;
          case 'airline':
            return row.airline_iata_code;
          case 'type':
            return (row.flight_type || '').toString();
          case 'status':
            return this.normalizeStatus(row.status) || safeStr(row.status);
          case 'city':
            return row.arrival_airport_city;
          case 'time':
            return row.estimated_flight_time;
          case 'gate':
            return row.gate;
          case 'baggage':
            return row.baggage;
          default:
            return '';
        }
      };
      const comparator = (a, b, idxA, idxB) => {
        const va = getVal(a, idxA);
        const vb = getVal(b, idxB);
        if (key === 'time') return cmpDate(va, vb) * dir;
        return cmpStr(va, vb) * dir;
      };
      return arr
        .map((row, idx) => ({ row, idx }))
        .sort((a, b) => comparator(a.row, b.row, a.idx, b.idx))
        .map(({ row }) => row);
    },
    selectedEnabled() {
      const st = this.getSelectedState();
      return !!(st && st.enabled);
    },
    selectedRemaining() {
      const st = this.getSelectedState();
      return st ? (st.remaining || 0) : 0;
    },
    selectedRowLabel() {
      if (!this.selectedRowData) return '-';
      const r = this.selectedRowData;
      return `${r.airline_iata_code || ''} ${r.flight_number || ''}`.trim();
    },
    progressStyle() {
      const total = this.actionWindowSeconds || 1;
      const rem = this.selectedRemaining;
      const pct = Math.max(0, Math.min(1, rem / total));
      const deg = Math.round(pct * 360);
      return {
        background: `conic-gradient(#1dd1a1 ${deg}deg, rgba(255,255,255,.12) ${deg}deg)`
      };
    },
    selectedClaimTimes() {
      const empty = { firstBag: null, lastBag: null };
      if (!this.selectedRowKey) return empty;
      const map = this.claimTimes || {};
      return map[this.selectedRowKey] || empty;
    }
  },
  mounted() {
    // Decrypt permissions from localStorage
    let mkLocalData = localStorage.getItem('Permissions');
    if (!mkLocalData) {
      mkLocalData = 'null';
    }
    const secretPermissionKey = process.env.KEY || 'myscecretkey';
    this.permissions = decryptData(mkLocalData, secretPermissionKey) || [];

    // Determine access
    const p = this.permissions;
    this.canViewGates = Array.isArray(p) && (p.includes('View-Gate') || p.includes('Gate-Operator'));
    this.canViewClaims = Array.isArray(p) && (p.includes('View-Claim') || p.includes('Claim-Operator'));

    // Default active selector: prefer 'gate' when both are available
    if (this.canViewGates && this.canViewClaims) {
      this.currentSelector = 'gate';
    } else if (this.canViewGates) {
      this.currentSelector = 'gate';
    } else if (this.canViewClaims) {
      this.currentSelector = 'claim';
    }

    this.fetchData();
  },
  methods: {
    refreshPortalPage() {
      this.fetchData();
    },
    devLog(...args) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.log(...args);
      }
    },
    buildResultSortCache() {
      const cache = {};
      const safeStr = v => (v == null ? '' : String(v));
      (this.result || []).forEach((row, idx) => {
        const k = this.rowKey(row, idx);
        cache[k] = {
          flight: `${safeStr(row.airline_iata_code)} ${safeStr(row.flight_number)}`,
          airline: row.airline_iata_code,
          type: (row.flight_type || '').toString(),
          status: this.normalizeStatus(row.status) || safeStr(row.status),
          city: row.arrival_airport_city,
          time: row.estimated_flight_time,
          gate: row.gate,
          baggage: row.baggage,
        };
      });
      this.resultSortCache = cache;
    },
    updateOperatorSort(key) {
      if (this.opSortKey === key) {
        this.opSortDir = this.opSortDir === 'asc' ? 'desc' : 'asc';
      } else {
        this.opSortKey = key;
        this.opSortDir = 'asc';
      }
    },
    selectRow(row, idx) {
      this.selectedRowKey = this.rowKey(row, idx);
      this.selectedRowIndex = idx;
      this.selectedRowData = row;
      // ensure state exists to immediately reflect selection
      this.ensureRowState(this.selectedRowKey);
    },
    onGateChange() {
      if (!this.selectedGate) return;
      // restart polling for new selection
      this.stopPolling();
      this.queryOperator('gate', this.selectedGate);
    },
    onClaimChange() {
      if (!this.selectedClaim) return;
      // restart polling for new selection
      this.stopPolling();
      this.queryOperator('claim', this.selectedClaim);
    },
    // Switch active selector between 'gate' and 'claim'
    activateSelector(mode) {
      if (this.currentSelector === mode) return;
      // Stop any ongoing polling
      this.stopPolling();
      // Reset table/result and row selection/action states
      this.resetRowActions();
      this.selectedRowKey = null;
      this.selectedRowIndex = null;
      this.selectedRowData = null;
      this.result = [];
      this.resultMode = null;
      // Clear the opposite dropdown's selection so it visually deselects
      if (mode === 'gate') {
        this.selectedClaim = null;
      } else if (mode === 'claim') {
        this.selectedGate = null;
      }
      this.currentSelector = mode;
    },
    // Start/stop polling based on current selection and interval
    startPolling() {
      this.stopPolling();
      if (!this.flightDetailsPollSec || this.flightDetailsPollSec <= 0) return;
      const mode = this.resultMode;
      const value = mode === 'gate' ? this.selectedGate : mode === 'claim' ? this.selectedClaim : null;
      if (!mode || !value) return;
      this.devLog('[POLL] start', { intervalSec: this.flightDetailsPollSec, mode, value });
      this.pollTimer = setInterval(() => {
        // lightweight refresh indicators per mode
        if (mode === 'gate') this.gateRefreshing = true; else if (mode === 'claim') this.claimRefreshing = true;
        const startedAt = new Date();
        this.devLog('[POLL] tick', { mode, value, startedAt: startedAt.toISOString() });
        this.queryOperator(mode, value, { preserveActions: true, fromPoll: true })
          .then(() => {
            const endedAt = new Date();
            this.devLog('[POLL] done', { mode, value, ms: endedAt - startedAt });
          })
          .catch((e) => {
            // eslint-disable-next-line no-console
            console.warn('[POLL] error', e);
          })
          .finally(() => {
            setTimeout(() => {
              if (mode === 'gate') this.gateRefreshing = false; else if (mode === 'claim') this.claimRefreshing = false;
            }, 250);
          });
      }, this.flightDetailsPollSec * 1000);
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },
    parseEstimatedFlightTime(estRaw) {
      if (!estRaw) return null;
      const raw = String(estRaw);
      const shouldTreatAsLocal = this.treatZuluAsLocalTime && /Z$/i.test(raw);
      const normalized = shouldTreatAsLocal ? raw.replace(/Z$/i, '') : raw;
      const d = new Date(normalized);
      if (isNaN(d.getTime())) return null;
      return { date: d, treatedAsLocal: shouldTreatAsLocal, normalized };
    },
    // Return true if row's estimated_flight_time is within configured window for current mode
    isWithinTimeWindow(row, mode) {
      const estRaw = row.estimated_flight_time;
      // In claim mode, allow rows without estimated time to pass (backend may omit it)
      // if (!estRaw) return mode === 'claim';
      if (!estRaw) return false;
      const parsed = this.parseEstimatedFlightTime(estRaw);
      if (!parsed) return false;
      const est = parsed.date;
      const now = new Date();
      const rollInMin = mode === 'claim' ? this.rollInClaimMin : this.rollInGateMin;
      const rollOutMin = mode === 'claim' ? this.rollOutClaimMin : this.rollOutGateMin;
      const showFrom = new Date(est.getTime() - rollInMin * 60 * 1000);
      const hideAfter = new Date(est.getTime() + rollOutMin * 60 * 1000);
      const within = now >= showFrom && now <= hideAfter;
      this.devLog('[TIMEWINDOW]', {
        mode,
        estRaw,
        within,
        flight: row.flight_number || row.flightNumber,
      });
      return within;
    },
    // Map Gate UI action names to backend status codes
    mapGateStatus(name) {
      switch (name) {
        case 'Last Call': return 'FinalCall';
        case 'Gates Closed':
        case 'Gate Closed':
        case 'Closed': return 'GateClosed';
        case 'Cancelled': return 'Cancelled';
        case 'Boarding': return 'Boarding';
        default: return name;
      }
    },
    // Normalize various possible flight_type fields to lowercase string
    getRowFlightType(row) {
      const ft = row.flight_type || row.flightType || row.flighttype || row.type || '';
      return String(ft).toLowerCase();
    },
    async queryOperator(mode, value, opts = {}) {
      const preserve = !!opts.preserveActions;
      const fromPoll = !!opts.fromPoll;
      // For poll ticks OR preserveActions (refresh after update), avoid triggering full-table loading state
      const isRefresh = fromPoll || preserve;
      this.queryLoading = isRefresh ? false : true;
      if (fromPoll) this.headerRefreshing = true;
      this.resultMode = mode;
      // Clear previous result and row actions unless preserving (e.g., claim refresh)
      if (!preserve) {
        this.resetRowActions();
        this.selectedRowKey = null;
        this.selectedRowIndex = null;
        this.selectedRowData = null;
      }
      // Do not blank the table during refresh operations; only clear on initial/mode-change loads
      if (!isRefresh) this.result = [];
      this.errors = [];

      const url = (this.$hostName || '') + '/operator/query';
      const payload = { mode, value };
      try {
        const { data } = await axios.post(url, payload);
        // Normalize response shape
        this.resultMode = (data && data.mode) || mode;
        this.serverCount = (data && data.count) || (Array.isArray(data && data.data) ? data.data.length : 0);
        this.result = (data && data.data) || [];
        const beforeFilterCount = Array.isArray(this.result) ? this.result.length : 0;
        // Filter by flight_type depending on mode
        if (this.resultMode === 'gate') {
          // Accept common variants for departures (e.g., D, Dep, Departure, Departures)
          this.result = this.result.filter(r => {
            const ft = this.getRowFlightType(r);
            const isDep = ft.startsWith('d') || ft.includes('dep') || ft === 'departure' || ft === 'departures';
            return isDep;
          });
        } else if (this.resultMode === 'claim') {
          // Accept common variants for arrivals (e.g., A, Arr, Arrival, Arrivals)
          this.result = this.result
            .filter(r => {
              const ft = this.getRowFlightType(r);
              const isArr = ft.startsWith('a') || ft.includes('arr') || ft === 'arrival' || ft === 'arrivals';
              return isArr;
            })
            // Only show rows whose status is InGate (tolerate spaces/underscores and case)
            .filter(r => {
              const raw = r && r.status ? String(r.status) : '';
              const s = raw.toLowerCase().replace(/\s|_/g, '');
              const isInGate = s.includes('ingate');
              return isInGate;
            });
        }
        // Filter by estimated_flight_time roll-in/out window
        // In claim mode, if the row has no estimated time, still display it
        this.result = this.result.filter(r => this.isWithinTimeWindow(r, this.resultMode));
        const afterFilterCount = Array.isArray(this.result) ? this.result.length : 0;
        this.resultCount = afterFilterCount;
        this.devLog('[FILTER]', {
          mode: this.resultMode,
          beforeFilterCount,
          afterFilterCount,
        });
        this.buildResultSortCache();
        // Mark newly added rows (only animate adds, not updates). Use current result set keys vs knownKeys.
        const newFlags = {};
        const nextKnown = { ...this.knownKeys };
        if (Array.isArray(this.result)) {
          this.result.forEach((row, i) => {
            const k = this.rowKey(row, i);
            if (!this.knownKeys[k]) {
              newFlags[k] = true;
            }
            nextKnown[k] = true;
          });
        }
        // Apply new flags and clear them after a brief highlight duration
        this.justAdded = newFlags;
        if (Object.keys(newFlags).length) {
          setTimeout(() => { this.justAdded = {}; }, 1200);
        }
        // Update knownKeys for next diff
        this.knownKeys = nextKnown;
        // Preselect action based on current status for gate/claim modes
        if (this.resultMode === 'gate' || this.resultMode === 'claim') {
          this.result.forEach((row, idx) => {
            const key = this.rowKey(row, idx);
            const st = this.ensureRowState(key);
            const normalized = this.normalizeStatus(row.status);
            if (normalized) {
              st.selected = normalized;
              if (!st.history || st.history.length === 0) {
                st.history = [normalized];
              }
            }
          });
        }
        // If preserving actions, try to keep the previously selected row attached
        if (preserve && this.selectedRowKey) {
          // Attempt to find the same key in the new results and rebind selectedRowData/index
          for (let i = 0; i < this.result.length; i++) {
            const r = this.result[i];
            if (this.rowKey(r, i) === this.selectedRowKey) {
              this.selectedRowIndex = i;
              this.selectedRowData = r;
              break;
            }
          }
        }
        // After claim data is shown, also fetch baggage details
        if (this.resultMode === 'claim') {
          this.fetchBaggageAll().catch(() => {});
        }
        // Initialize default operator sort (Flight Time ASC) on first load per mode
        const m = this.resultMode;
        if (m === 'gate' || m === 'claim') {
          if (!this.opSortInitByMode) this.opSortInitByMode = { gate: false, claim: false };
          if (!this.opSortInitByMode[m]) {
            this.opSortKey = 'time';
            this.opSortDir = 'asc';
            // Vue.set to keep it reactive if added at runtime
            if (this.$set) this.$set(this.opSortInitByMode, m, true); else this.opSortInitByMode[m] = true;
          }
        }
        // Start/restart polling after a successful fetch for the current mode/value (only when not invoked by poll tick)
        if (!fromPoll) this.startPolling();
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[OPERATOR] Query failed', { url, payload, err });
        const msg = this.extractMessage(err, 'Failed to fetch results');
        this.errors = [msg];
      } finally {
        this.queryLoading = false;
        if (fromPoll) this.headerRefreshing = false;
        if (!this.result.length) {
          this.resultSortCache = {};
        }
      }
    },
    resetRowActions() {
      // Stop and clear all timers, then reset the map
      Object.values(this.rowActions).forEach(st => {
        if (st && st.timer) {
          clearInterval(st.timer);
          st.timer = null;
        }
      });
      this.rowActions = {};
    },
    rowKey(row, idx) {
      return row.uid || row.flight_guid || `${row.airline_iata_code || ''}-${row.flight_number || ''}-${idx}`;
    },
    ensureRowState(key) {
      if (!this.rowActions[key]) {
        this.$set(this.rowActions, key, { enabled: false, selected: null, remaining: 0, timer: null, history: [] });
      } else if (!Array.isArray(this.rowActions[key].history)) {
        // Backfill history for existing state objects
        this.rowActions[key].history = [];
      }
      return this.rowActions[key];
    },
    isEnabled(row, idx) {
      const st = this.ensureRowState(this.rowKey(row, idx));
      return !!st.enabled;
    },
    remaining(row, idx) {
      const st = this.ensureRowState(this.rowKey(row, idx));
      return st.remaining || 0;
    },
    // Returns true if the provided ISO timestamp is from 'today' in local time
    isToday(ts) {
      if (!ts) return false;
      try {
        const d = new Date(ts);
        const now = new Date();
        return (
          d.getFullYear() === now.getFullYear() &&
          d.getMonth() === now.getMonth() &&
          d.getDate() === now.getDate()
        );
      } catch (e) { return false; }
    },
    // Prune local claimTimes to only keep entries from today
    pruneClaimTimes() {
      const map = this.claimTimes || {};
      const next = { ...map };
      Object.keys(map).forEach(k => {
        const cur = map[k] || {};
        const fb = this.isToday(cur.firstBag) ? cur.firstBag : null;
        const lb = this.isToday(cur.lastBag) ? cur.lastBag : null;
        if (!fb && !lb) {
          delete next[k];
        } else {
          next[k] = { firstBag: fb, lastBag: lb };
        }
      });
      this.claimTimes = next;
      this.persistClaimTimes();
    },
    // Persist local claim first/last bag timestamps
    persistClaimTimes() {
      try { localStorage.setItem('claimTimesV1', JSON.stringify(this.claimTimes || {})); } catch (e) { /* ignore */ }
    },
    setClaimTimeForKey(key, which, ts) {
      if (!key) return;
      const cur = this.claimTimes[key] || { firstBag: null, lastBag: null };
      const next = { ...cur };
      if (which === 'First Belt') next.firstBag = ts;
      if (which === 'Last Belt') next.lastBag = ts;
      this.$set(this.claimTimes, key, next);
      // Persist and prune older-than-today entries to avoid localStorage growth
      this.persistClaimTimes();
      this.pruneClaimTimes();
    },
    clearClaimTimeForKey(key, which) {
      if (!key) return;
      const cur = this.claimTimes[key] || { firstBag: null, lastBag: null };
      const next = { ...cur };
      if (which === 'First Belt') next.firstBag = null;
      if (which === 'Last Belt') next.lastBag = null;
      this.$set(this.claimTimes, key, next);
      this.persistClaimTimes();
      this.pruneClaimTimes();
    },
    // Clear ALL claim times across all flights for the given status label
    clearAllClaimTimes(which) {
      const map = this.claimTimes || {};
      const next = { ...map };
      Object.keys(next).forEach(k => {
        const cur = next[k] || { firstBag: null, lastBag: null };
        if (which === 'First Belt') cur.firstBag = null;
        if (which === 'Last Belt') cur.lastBag = null;
        next[k] = cur;
      });
      this.claimTimes = next;
      this.persistClaimTimes();
      this.pruneClaimTimes();
    },
    formatTs(ts) {
      if (!ts) return '—';
      try { return moment(ts).format('YYYY-MM-DD HH:mm:ss'); } catch (e) { return ts; }
    },
    toggleEnable(row, idx) {
      const key = this.rowKey(row, idx);
      const st = this.ensureRowState(key);
      if (st.enabled) {
        // turn off - but keep st.selected so the wedge stays highlighted
        st.enabled = false;
        st.remaining = 0;
        if (st.timer) { clearInterval(st.timer); st.timer = null; }
      } else {
        // Trigger unlock animation
        this.isUnlocking = true;
        // Delay enabling to allow unlock animation to complete (600ms animation duration)
        setTimeout(() => {
          this.isUnlocking = false;
          // turn on with countdown
          st.enabled = true;
          st.remaining = this.actionWindowSeconds;
          if (st.timer) { clearInterval(st.timer); st.timer = null; }
          st.timer = setInterval(() => {
            // Use the current reactive object from the map to avoid stale closure
            const cur = this.rowActions[key];
            if (!cur) return;
            cur.remaining = (cur.remaining || 0) - 1;
            if (cur.remaining <= 0) {
              clearInterval(cur.timer);
              cur.timer = null;
              cur.enabled = false;
              cur.remaining = 0;
            }
            // Force re-render to ensure the custom switch reflects state immediately
            this.$forceUpdate();
          }, 1000);
        }, 650); // Slightly longer than animation (600ms) for smooth transition
      }
      // Do not replace the object to keep the interval bound to the reactive instance
    },
    actionClass(row, idx, name) {
      const key = this.rowKey(row, idx);
      const st = this.ensureRowState(key);
      const base = 'btn-outline-secondary';
      const active = 'btn-primary';
      return st.selected === name ? active : base;
    },
    selectAction(row, idx, name) {
      const key = this.rowKey(row, idx);
      const st = this.ensureRowState(key);
      if (!st.enabled) return;
      // Determine the status to send considering revert behavior on Cancelled
      let sendName = name;
      // HANDLE GATE CANCEL FIRST to avoid generic history-based revert overriding the desired mapping
      if (this.resultMode === 'gate' && name === 'Cancelled') {
        const currentLabel = (st && st.selected) || (row && row.status) || '';
        const cur = String(currentLabel).toLowerCase();
        if (cur.includes('gate') && (cur.includes('closed') || cur.includes('close'))) {
          // Gate Closed -> Final Call
          sendName = 'Last Call';
          st.selected = 'Last Call';
        } else if (cur.includes('final') || cur.includes('last')) {
          // Final Call -> Boarding
          sendName = 'Boarding';
          st.selected = 'Boarding';
        } else if (cur.includes('board')) {
          // Boarding -> Scheduled (backend), but keep UI wedge as Cancel selected
          sendName = 'Scheduled';
          st.selected = 'Cancelled';
        } else {
          // Unknown -> Cancelled
          sendName = 'Cancelled';
          st.selected = 'Cancelled';
        }
        // proceed to post below
      } else if (name === 'Cancelled') {
        // Generic cancel (non-gate): revert to previous where applicable
        if (Array.isArray(st.history) && st.history.length >= 2) {
          // Revert one step: pop current selection, use previous
          st.history.pop();
          const prev = st.history[st.history.length - 1];
          sendName = prev || name;
          st.selected = prev || st.selected;
        } else if (Array.isArray(st.history) && st.history.length === 1) {
          // Only one item in history; revert to that
          const prev = st.history[0];
          sendName = prev || name;
          st.selected = prev || st.selected;
        } else {
          // No history; fall back to current selected
          sendName = st.selected || name;
        }
      } else {
        // Normal selection path: push to history if different
        if (!Array.isArray(st.history)) st.history = [];
        const last = st.history[st.history.length - 1];
        if (last !== name) st.history.push(name);
        st.selected = name;
      }
      // Log which gate wedge was clicked and what will be sent
      // eslint-disable-next-line no-console
      console.log('[GATE]', 'Clicked wedge:', name, 'Send:', sendName, 'Flight:', row.airline_iata_code, row.flight_number);
      // Send update to flights/edit (gates)
      if (this.resultMode === 'gate') {
        this.postEdit(row, sendName)
          .then(async () => {
            // Auto-disable (lock) after successful update, preserve selected
            try {
              const s = this.ensureRowState(key);
              s.enabled = false;
              s.remaining = 0;
              if (s.timer) { clearInterval(s.timer); s.timer = null; }
            } catch (e) { /* noop */ }
            // Refresh gate list to reflect latest server state
            if (this.selectedGate) {
              this.gateRefreshing = true;
              try {
                await this.queryOperator('gate', this.selectedGate, { preserveActions: true });
              } finally {
                setTimeout(() => { this.gateRefreshing = false; }, 350);
              }
            }
          })
          .catch(() => {});
      }
    },
    // Radial helpers bound to currently selected row
    radialClass(name) {
      const st = this.getSelectedState();
      const classes = [];
      if (st && st.selected === name) classes.push('active');
      if (!this.selectedEnabled || !this.selectedRowKey) classes.push('disabled');
      return classes.join(' ');
    },
    radialClassClaim(name) {
      const st = this.getSelectedState();
      const classes = [];
      if (st && st.selected === name) classes.push('active');
      if (!this.selectedEnabled || !this.selectedRowKey) classes.push('disabled');
      return classes.join(' ');
    },
    toggleSelectedEnable() {
      if (!this.selectedRowKey) return;
      const idx = this.selectedRowIndex;
      const row = this.selectedRowData;
      if (!row) return;
      this.toggleEnable(row, idx);
    },
    actionOption(name) {
      if (!this.selectedRowKey) return;
      const idx = this.selectedRowIndex;
      const row = this.selectedRowData;
      if (!row) return;
      this.selectAction(row, idx, name);
    },
    getSelectedState() {
      if (!this.selectedRowKey) return null;
      return this.ensureRowState(this.selectedRowKey);
    },
    onTriClick(name) {
      if (!this.selectedRowKey) return;
      const idx = this.selectedRowIndex;
      const row = this.selectedRowData;
      if (!row) return;
      const key = this.rowKey(row, idx);
      const st = this.ensureRowState(key);
      if (!st.enabled) return;
      // Determine send name with revert behavior for Cancelled
      let sendName = name;
      if (name === 'Cancelled') {
        // Updated rule for claims:
        // - If current status is Last Bag -> revert to First Belt AND clear Last Bag timestamp
        // - If current status is First Bag -> revert to Cancelled AND clear First Bag timestamp
        const currentLabel = (st && st.selected)
          || (row && row._claimStatus)
          || (row && row.status) || '';
        const cur = String(currentLabel).toLowerCase();
        const onlyKey = this.selectedRowKey || key;
        if (cur.includes('last')) {
          sendName = 'First Belt';
          st.selected = 'First Belt';
          // Clear Last Bag timestamp for this flight only
          this.clearClaimTimeForKey(onlyKey, 'Last Belt');
        } else if (cur.includes('first')) {
          sendName = 'Cancelled';
          st.selected = 'Cancelled';
          // Clear First Bag timestamp for this flight only
          this.clearClaimTimeForKey(onlyKey, 'First Belt');
        } else {
          // Fallback: if unknown, cancel without altering local timestamps
          sendName = 'Cancelled';
          st.selected = 'Cancelled';
        }
        // Always suppress local time to keep last-updated values unchanged (we explicitly cleared what was needed above)
        // eslint-disable-next-line no-console
        console.log('[CLAIM] Cancel rule applied. Current:', currentLabel, 'Sending:', sendName);
        this.postClaimStatus(row, sendName, { suppressLocalTime: true }).catch(() => {});
        return;
      } else {
        if (!Array.isArray(st.history)) st.history = [];
        const last = st.history[st.history.length - 1];
        if (last !== name) st.history.push(name);
        st.selected = name;
      }
      // eslint-disable-next-line no-console
      console.log('[CLAIM]', 'Clicked wedge:', name, 'Send:', sendName, 'Flight:', row.airline_iata_code, row.flight_number);
      this.postClaimStatus(row, sendName).catch(() => {});
    },
    async postClaimStatus(row, actionName, opts = {}) {
      try {
        const statusMap = {
          'First Belt': 'first bag',
          'Last Belt': 'last bag',
          'Cancelled': 'cancelled',
        };
        const status = statusMap[actionName] || actionName;
        const flight_guid = row.flight_guid || row.guid || row.uid || row.id;
        if (!flight_guid) {
          // eslint-disable-next-line no-console
          console.warn('[CLAIM] Missing flight_guid for payload');
          return;
        }
        const url = (this.$hostName || '') + '/baggage/updateStatusByFlightGuid';
        const payload = { flight_guid, status };
        await axios.post(url, payload, { headers: { 'Content-Type': 'application/json' } });
        // eslint-disable-next-line no-console
        console.log('[CLAIM] POST OK', url, payload);
        // Compute the key once for subsequent local operations
        const key = this.selectedRowKey || this.rowKey(row, this.selectedRowIndex || 0);
        // Record local timestamp unless suppressed (e.g., revert via Cancel)
        if (!opts || !opts.suppressLocalTime) {
          const nowTs = new Date().toISOString();
          this.setClaimTimeForKey(key, actionName, nowTs);
        }
        // Auto-disable (lock) after successful update, preserve selected
        try {
          const s = this.ensureRowState(key);
          s.enabled = false;
          s.remaining = 0;
          if (s.timer) { clearInterval(s.timer); s.timer = null; }
        } catch (e) { /* noop */ }
        // Refresh claim list to reflect latest server state
        if (this.resultMode === 'claim' && this.selectedClaim) {
          this.claimRefreshing = true;
          try {
            await this.queryOperator('claim', this.selectedClaim, { preserveActions: true });
          } finally {
            // small delay for nicer UX
            setTimeout(() => { this.claimRefreshing = false; }, 350);
          }
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[CLAIM] POST failed', err);
        this.errors = [this.extractMessage(err, 'Failed to update baggage status')];
      }
    },
    
    normalizeStatus(status) {
      if (!status) return null;
      const raw = String(status);
      const s = raw.toLowerCase();
      const compact = s.replace(/\s|_/g, '');
      // Gate statuses (accept both human names and backend codes)
      if (compact.includes('finalcall') || (s.includes('last') && s.includes('call'))) return 'Last Call';
      if (compact.includes('gateclosed') || (s.includes('gate') && s.includes('closed')) || s === 'closed') return 'Gates Closed';
      if (s.includes('boarding')) return 'Boarding';
      if (s.includes('cancel')) return 'Cancelled';
      // Claim statuses
      if (s.includes('first') && s.includes('belt')) return 'First Belt';
      if (s.includes('last') && s.includes('belt')) return 'Last Belt';
      return null;
    },
    buildEditPayload(row, actionName) {
      // Gate updates: send only the minimal required fields to backend
      // Avoid sending null/unused fields like estimated, gate, claim, etc.
      const gateStatus = this.mapGateStatus(actionName);
      return [{
        id: row.uid || row.id,
        status: gateStatus,
      }];
    },
    async postEdit(row, actionName) {
      const url = (this.$hostName || '') + '/flights/edit';
      const payload = this.buildEditPayload(row, actionName);
      try {
        await axios.put(url, payload);
      } catch (e) {
        // surface an inline error if needed
        // this.errors = [this.extractMessage(e, 'Failed to update flight')];
      }
    },
    async fetchBaggageAll() {
      const url = (this.$hostName || '') + '/baggage/all';
      try {
        const { data } = await axios.get(url);
        const list = Array.isArray(data) ? data : (data && data.data) || [];
        this.baggageAll = list;
        // eslint-disable-next-line no-console
        console.log('[CLAIM] baggage/all fetched:', Array.isArray(list) ? list.length : 0);
        // Apply statuses to current claim results if any
        if (this.resultMode === 'claim' && Array.isArray(this.result) && this.result.length) {
          this.applyBaggageStatusToResults();
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[CLAIM] Failed to fetch baggage/all', err);
      }
    },
    // Determine a stable GUID for a row
    getRowGuid(row) {
      return row && (row.flight_guid || row.guid || row.uid || row.id);
    },
    // Compute claim status label from baggage record
    computeClaimStatus(bag) {
      if (!bag) return null;
      // API uses *_bag_status fields (0/1)
      const first = Number(bag.first_bag_status != null ? bag.first_bag_status : (bag.first_baggage_belt || bag.first_bag || bag.first || 0));
      const last = Number(bag.last_bag_status != null ? bag.last_bag_status : (bag.last_baggage_belt || bag.last_bag || bag.last || 0));
      if (!first && !last) return 'Cancelled';
      if (first) return 'First Belt';
      if (!first && last) return 'Last Belt';
      return null;
    },
    // Apply baggage status to current results and preselect corresponding wedge
    applyBaggageStatusToResults() {
      if (!Array.isArray(this.result) || !this.result.length) return;
      if (!Array.isArray(this.baggageAll) || !this.baggageAll.length) return;
      // Build a quick lookup by guid
      const byGuid = new Map();
      this.baggageAll.forEach(b => {
        const k = b.flight_guid || b.guid || b.uid || b.id;
        if (k) byGuid.set(String(k).trim().toLowerCase(), b);
      });
      this.result.forEach((row, idx) => {
        const guid = this.getRowGuid(row);
        if (!guid) return;
        const bag = byGuid.get(String(guid).trim().toLowerCase());
        const label = this.computeClaimStatus(bag);
        if (label) {
          // Save a computed status for table display
          this.$set(row, '_claimStatus', label);
          // Preselect wedge for this row
          const key = this.rowKey(row, idx);
          const st = this.ensureRowState(key);
          st.selected = label;
        } else {
          // eslint-disable-next-line no-console
          console.debug('[CLAIM] No baggage match for guid', guid);
        }
      });
      // Re-render to reflect highlights in radial
      this.$forceUpdate();
    },
    // Returns the status to display in the table row
    displayStatus(row) {
      // For claim mode, show '-' until baggage-derived _claimStatus is available to avoid flicker of 'InGate'
      if (this.resultMode === 'claim') {
        const claimRaw = row && row._claimStatus ? row._claimStatus : null;
        const s = (claimRaw || '').toString().toLowerCase();
        if (!s) return '-';
        if (s.includes('first') && (s.includes('belt') || s.includes('bag'))) return 'First Bag';
        if (s.includes('last') && (s.includes('belt') || s.includes('bag'))) return 'Last Bag';
        if (s.includes('cancel')) return '-';
        // Fallback to showing the raw computed claim status if it doesn't match known labels
        return claimRaw || '-';
      }

      // For gate mode and others, keep existing behavior (may use row.status)
      const raw = (row && row.status ? row.status : null);
      if (this.resultMode === 'gate') {
        const gs = (raw || '').toString().toLowerCase();
        // Show 'Scheduled' when backend shows Cancelled for gate rows, per requirement
        if (gs.includes('cancel')) return 'Scheduled';
      }
      return raw || '-';
    },
    async fetchData() {
      this.loading = true;
      this.errors = [];
      const errs = [];

      // Use the configured API base
      const base = this.$hostName || '';
      // According to your description and existing config, these endpoints are under /airports
      const gatesUrl = base + '/airports/gates';
      const claimsUrl = base + '/airports/claims';

      const reqs = [];
      if (this.canViewGates) {
        reqs.push(
          axios.get(gatesUrl)
            .then(res => ({ kind: 'gates', res }))
            .catch(err => { errs.push(this.extractMessage(err, 'Failed to load gates')); return { kind: 'gates', res: { data: [] } }; })
        );
      }
      if (this.canViewClaims) {
        reqs.push(
          axios.get(claimsUrl)
            .then(res => ({ kind: 'claims', res }))
            .catch(err => { errs.push(this.extractMessage(err, 'Failed to load claims')); return { kind: 'claims', res: { data: [] } }; })
        );
      }

      try {
        const results = await Promise.all(reqs);
        results.forEach(({ kind, res }) => {
          const list = Array.isArray(res.data) ? res.data : (res.data && res.data.data) || [];
          if (kind === 'gates') this.gates = list;
          if (kind === 'claims') this.claims = list;
        });
      } finally {
        this.loading = false;
        this.errors = errs;
      }
    },
    extractMessage(error, fallback) {
      const apiErrors = error && error.response && error.response.data && error.response.data.errors;
      if (Array.isArray(apiErrors) && apiErrors.length) return apiErrors[0];
      if (typeof apiErrors === 'string') return apiErrors;
      return fallback;
    },
  },
};
</script>

<style scoped>
.operator-view .hero {
  border-radius: 12px;
  background: linear-gradient(135deg, #4b6cb7 0%, #182848 100%);
}
.operator-view .hero h2 {
  font-size: 1.5rem;
  margin-bottom: 0.25rem;
}
.operator-view .hero p {
  font-size: 0.875rem;
}

.gradient-header {
  background: linear-gradient(135deg, #6a11cb 0%, #2575fc 100%);
}

.gradient-header-2 {
  background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
}

.gradient-header-soft {
  background: linear-gradient(135deg, #f7971e 0%, #ffd200 100%);
}

/* Removed - now defined in card-header section */

.animate-up {
  animation: slideUp 420ms ease-out both;
}

.animate-in {
  animation: fadeIn 380ms ease-out both;
}

@keyframes slideUp {
  from { transform: translateY(12px); opacity: 0; }
  to   { transform: translateY(0); opacity: 1; }
}

@keyframes fadeIn {
  from { opacity: 0; }
  to   { opacity: 1; }
}

.fancy-select {
  border-radius: 8px;
  border: 1px solid #e9ecef;
  transition: box-shadow .2s ease, border-color .2s ease;
  padding: 0.4rem 0.6rem;
  font-size: 0.875rem;
  height: auto;
}
.fancy-select:focus {
  border-color: #6a11cb;
  box-shadow: 0 0 0 0.15rem rgba(106,17,203,.12);
}
.card-body {
  padding: 0.5rem 0.75rem;
}
.card-header {
  padding: 0.35rem 0.75rem;
  font-size: 0.85rem;
  min-height: auto;
}
.card-header .mdi {
  font-size: 16px;
}
.card-header strong {
  font-size: 0.85rem;
}
.form-label {
  font-size: 0.8rem;
  margin-bottom: 0.25rem;
  font-weight: 600;
}
.form-label .mdi {
  font-size: 14px;
}

.pretty-table {
  table-layout: fixed;
  width: 100%;
}
.pretty-table thead tr {
  background: linear-gradient(135deg, #ece9e6 0%, #ffffff 100%);
  height: 44px;
}
.pretty-table .portal-table-body--loading .portal-skeleton-row:hover {
  background-color: inherit;
}
.pretty-table tbody tr {
  transition: background-color .2s ease, transform .1s ease;
}
.pretty-table tbody tr:hover {
  background-color: rgba(99, 149, 255, 0.08);
}
.pretty-table td, .pretty-table th {
  vertical-align: middle !important;
}
.results-wrapper {
  overflow: hidden;
  border-bottom-left-radius: .5rem;
  border-bottom-right-radius: .5rem;
}
/* Scrollable results area */
.results-scroll { 
  max-height: 70vh; 
  overflow-y: auto;
  overflow-x: hidden;
}
.results-scroll::-webkit-scrollbar {
  width: 8px;
}
.results-scroll::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}
.results-scroll::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}
.results-scroll::-webkit-scrollbar-thumb:hover {
  background: #555;
}
.results-wrapper .card-header {
  padding: 0.4rem 0.75rem;
}

/* Two-column layout for operator view */
.operator-grid { 
  align-items: flex-start; 
  margin-left: 0;
  margin-right: 0;
}
.operator-left-col {
  padding-left: 0;
  padding-right: 15px;
}
.operator-right-col {
  padding-left: 15px;
  padding-right: 0;
}

/* Radial footer styles */
.radial-footer {
  position: sticky;
  top: 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  z-index: 50;
  overflow: visible;
}

.radial-wrapper {
  --size: 420px;
  --ring-bg: #0a2542;
  --seg-color: #1e90ff;
  --seg-color-active: #2ed0ff;
  --seg-disabled: rgba(255,255,255,.18);
  /* tri-mode colors */
  --tri1: #1e90ff;
  --tri2: #4ecdc4;
  --tri3: #ff9f43;
  position: relative;
  width: var(--size);
  height: var(--size);
  border-radius: 50%;
  background: var(--ring-bg);
  box-shadow: 0 16px 36px rgba(0,0,0,.45), inset 0 0 0 8px rgba(0,0,0,.45);
  overflow: visible;
}

.radial-wrapper::after{
  /* Inner hole to create donut look */
  content: '';
  position: absolute;
  inset: 18%;
  border-radius: 50%;
  background: #0f3762;
  box-shadow: inset 0 0 0 10px rgba(0,0,0,.45);
  z-index: 1;
}

.segment {
  position: absolute;
  inset: 0;
  border: 0;
  background: var(--seg-color);
  /* Widen the ring so labels have more space */
  -webkit-mask: radial-gradient(circle at center, transparent 0 18%, #000 18.5% 100%);
          mask: radial-gradient(circle at center, transparent 0 18%, #000 18.5% 100%);
  border-radius: 50%;
  color: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding-top: 12px;
  transition: transform .2s ease, filter .2s ease, box-shadow .2s ease, opacity .2s ease;
  /* Light outer edge similar to Claim's stroke and a soft base shadow */
  box-shadow: 0 0 0 1px rgba(255,255,255,.28), 0 4px 10px rgba(0,0,0,.18);
  z-index: 2;
}

/* Text centered within each wedge */
.seg-text {
  position: absolute;
  color: #fff;
  font-weight: 800;
  font-size: 18px;
  letter-spacing: .25px;
  text-shadow: 0 1px 2px rgba(0,0,0,.45);
  transform: translate(-50%, -50%);
  pointer-events: none;
}
.seg1 .seg-text { top: 22%; left: 78%; }
.seg2 .seg-text { top: 78%; left: 78%; }
.seg3 .seg-text { top: 78%; left: 22%; }
.seg4 .seg-text { top: 22%; left: 22%; }

.segment .mdi { font-size: 24px; text-shadow: 0 2px 6px rgba(0,0,0,.35); }

.segment:hover { filter: brightness(1.12); transform: translateY(-1px); }

.segment.active {
  background: var(--seg-color-active);
  /* Slightly stronger edge and pop shadow on active to match Claim feel */
  box-shadow: 0 0 0 1.2px rgba(255,255,255,.35), 0 6px 14px rgba(0,0,0,.34), inset 0 0 0 2px rgba(255,255,255,.18);
  filter: brightness(1.06);
}

.segment.disabled { opacity: .35; pointer-events: none; }

.seg1 { clip-path: polygon(50% 50%, 100% 50%, 100% 0, 50% 0); }
.seg2 { clip-path: polygon(50% 50%, 100% 50%, 100% 100%, 50% 100%); }
.seg3 { clip-path: polygon(50% 50%, 50% 100%, 0 100%, 0 50%); }
.seg4 { clip-path: polygon(50% 50%, 0 50%, 0 0, 50% 0); }

/* Pop-out transforms for active gate wedges (reduced) */
.seg1.active { transform: translate(5px, -4px) scale(1.03); }
.seg2.active { transform: translate(4px, 3px) scale(1.02); }
.seg3.active { transform: translate(-4px, 3px) scale(1.02); }
.seg4.active { transform: translate(-3px, -4px) scale(1.02); }

/* Center button */
.center-btn {
  position: absolute;
  inset: 34%;
  border-radius: 50%;
  border: none;
  background: radial-gradient(circle at 30% 30%, #27a0ff, #0077ff);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 22px;
  box-shadow: 0 10px 24px rgba(0,0,0,.4), inset 0 0 0 8px rgba(0,0,0,.45);
  z-index: 5;
}

/* Tri-mode separators at 0, 120, 240 degrees */
/* Claim: keep clean ring (no partition lines) */
.radial-wrapper.tri-mode::before { display: none; }

.center-btn.enabled { background: radial-gradient(circle at 30% 30%, #00d4a0, #00b489); }
.center-btn.disabled { cursor: not-allowed; filter: grayscale(.2) brightness(.9); }

.center-btn .mdi { font-size: 32px; }

.progress-ring {
  position: absolute;
  inset: -12px;
  border-radius: 50%;
  background: conic-gradient(#1dd1a1 0deg, rgba(255,255,255,.12) 0deg);
  -webkit-mask: radial-gradient(circle at center, transparent 0 70%, #000 71% 100%);
          mask: radial-gradient(circle at center, transparent 0 70%, #000 71% 100%);
  z-index: -1;
  display: none;
}

.radial-label {
  position: absolute;
  padding: 4px 10px;
  border-radius: 14px;
  background: rgba(0,0,0,.35);
  color: #fff;
  font-weight: 800;
  font-size: 14px;
  line-height: 1;
  letter-spacing: .25px;
  text-shadow: 0 1px 2px rgba(0,0,0,.5);
  pointer-events: none;
  z-index: 5;
}
.label-top { top: 8%; left: 50%; transform: translateX(-50%); }
.label-bottom { bottom: 8%; left: 50%; transform: translateX(-50%); }
.label-left { left: 8%; top: 50%; transform: translateY(-50%); }
.label-right { right: 8%; top: 50%; transform: translateY(-50%); }

/* Keep icons visible but slightly inset */
.seg1 .mdi { position: absolute; top: 28%; left: 50%; transform: translateX(-50%); z-index: 4; }
.seg2 .mdi { position: absolute; bottom: 28%; left: 50%; transform: translateX(-50%); z-index: 4; }
.seg3 .mdi { position: absolute; right: 28%; top: 50%; transform: translateY(-50%); z-index: 4; }
.seg4 .mdi { position: absolute; left: 28%; top: 50%; transform: translateY(-50%); z-index: 4; }

.radial-caption { color: #7da2c7; }

/* Subtle separators to visually divide wedges */
.radial-wrapper::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background:
    linear-gradient(#ffffff55, #ffffff55) 50% 0/2px 100% no-repeat,
    linear-gradient(#ffffff55, #ffffff55) 0 50%/100% 2px no-repeat;
  -webkit-mask: radial-gradient(circle at center, transparent 0 28%, #000 28.5% 100%);
          mask: radial-gradient(circle at center, transparent 0 28%, #000 28.5% 100%);
  pointer-events: none;
  z-index: 1;
}

/* Selected row styling in table */
.outside-btn {
  position: absolute;
  padding: 8px 14px;
  border-radius: 18px;
  border: none;
  font-weight: 800;
  font-size: 13px;
  color: #0a2542;
  background: #ffffff;
  box-shadow: 0 6px 16px rgba(0,0,0,.25);
  z-index: 6;
}
.outside-btn:disabled { opacity: .5; cursor: not-allowed; }
.outside-top { top: -22px; left: 50%; transform: translateX(-50%); }
.outside-bottom { bottom: -22px; left: 50%; transform: translateX(-50%); }
.outside-left { left: -22px; top: 50%; transform: translateY(-50%); }
.outside-right { right: -22px; top: 50%; transform: translateY(-50%); }
.outside-btn.active { outline: 3px solid #2ed0ff; }
.outside-btn.disabled { pointer-events: none; }
.labels-overlay {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 4;
}
.pretty-table tr.selectable { cursor: pointer; }
.pretty-table tr.selected { outline-offset: -2px; background: linear-gradient(90deg, rgba(30,155,255,.12), rgba(46,208,255,.12)); transition: transform .15s ease; }

/* Claim mode: three option buttons around the ring */
.tri-seg {
  position: absolute;
  inset: 0;
  border: 0;
  background: conic-gradient(var(--seg-color) 0 120deg, transparent 120deg 360deg);
  -webkit-mask: radial-gradient(circle at center, transparent 0 24%, #000 24.5% 100%);
          mask: radial-gradient(circle at center, transparent 0 24%, #000 24.5% 100%);
  border-radius: 50%;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform .25s ease, filter .2s ease, opacity .2s ease;
  z-index: 3;
}
.t1 { background: conic-gradient(var(--tri1) 0 120deg, transparent 120deg 360deg); }
.t2 { background: conic-gradient(var(--tri2) 0 120deg, transparent 120deg 360deg); }
.t3 { background: conic-gradient(var(--tri3) 0 120deg, transparent 120deg 360deg); }
.tri-seg .tri-text {
  position: absolute;
  color: #0a2542;
  background: #fff;
  padding: 6px 12px;
  border-radius: 16px;
  font-weight: 800;
  font-size: 14px;
  letter-spacing: .25px;
  box-shadow: 0 4px 10px rgba(0,0,0,.25);
  white-space: nowrap;
}
.tri-seg.active { filter: brightness(1.15); box-shadow: inset 0 0 0 3px rgba(255,255,255,.35), 0 6px 16px rgba(46,208,255,.35); }
.tri-seg.disabled { opacity: .35; pointer-events: none; }
.t1 { transform: rotate(0deg); }
.t2 { transform: rotate(120deg); }
.t3 { transform: rotate(240deg); }
/* Position labels at the visual center of each wedge without rotation complexity */
.t1 .tri-text { top: 26%; left: 50%; transform: translate(-50%, -50%); }
.t2 .tri-text { top: 70%; left: 27%; transform: translate(-50%, -50%); }
.t3 .tri-text { top: 70%; left: 73%; transform: translate(-50%, -50%); }

/* SVG-based claim radial styling */
.tri-svg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  z-index: 3;
  /* Match Gate ring thickness: remove center area by mask */
  -webkit-mask: radial-gradient(circle at center, transparent 0 18%, #000 18.5% 100%);
          mask: radial-gradient(circle at center, transparent 0 18%, #000 18.5% 100%);
}
.tri-svg .sec { cursor: pointer; transition: transform .2s ease, filter .2s ease; transform-origin: 50% 50%; }
.tri-svg .sec path { fill: var(--seg-color); stroke: rgba(255,255,255,.35); stroke-width: 0.6; transition: stroke-width .2s ease, filter .2s ease, fill .2s ease; }
.tri-svg .sec.disabled { opacity: .35; pointer-events: none; }
.tri-svg .sec.active path { fill: var(--seg-color-active); filter: brightness(1.06); stroke-width: 1.2; }
/* Pop the selected wedge slightly outward with a soft shadow */
.tri-svg .sec.active { filter: drop-shadow(0 4px 10px rgba(0,0,0,.35)); }
/* Per-sector outward nudges so the pop-out is radial (match Gate feel) */
.tri-svg .sec.s1.active { transform: translate(3px, 0) scale(1.02); }
.tri-svg .sec.s2.active { transform: translate(-3px, 2px) scale(1.02); }
.tri-svg .sec.s3.active { transform: translate(-2px, -3px) scale(1.02); }
.tri-svg .tri-label { fill: #ffffff; font-weight: 800; font-size: 8.5px; text-shadow: 0 1px 2px rgba(0,0,0,.45); }

/* Claims: reduce inner spacing for non-selected wedges when engaged */
.radial-wrapper.tri-mode.claim-engaged .tri-svg .sec:not(.active) {
  transform: scale(0.98);
  filter: brightness(0.96);
}

/* Claim refreshing overlay + pulse */
.radial-wrapper.refreshing::after {
  box-shadow: inset 0 0 0 10px rgba(0,0,0,.45), 0 0 0 0 rgba(46,208,255,.45);
  animation: ringPulse 0.6s ease-out 2;
}
.refresh-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  z-index: 6;
}
@keyframes ringPulse {
  0% { box-shadow: inset 0 0 0 10px rgba(0,0,0,.45), 0 0 0 0 rgba(46,208,255,.0); }
  50% { box-shadow: inset 0 0 0 10px rgba(0,0,0,.45), 0 0 0 10px rgba(46,208,255,.25); }
  100% { box-shadow: inset 0 0 0 10px rgba(0,0,0,.45), 0 0 0 0 rgba(46,208,255,.0); }
}

/* Gate-only spacing: increase inner and outer gaps around wedges (claims unchanged) */
.radial-wrapper.gate-mode::after { inset: 28%; }
.radial-wrapper.gate-mode .segment {
  /* Inner gap 24%, outer gap ~4% (clip at 96%) */
  -webkit-mask: radial-gradient(circle at center,
                     transparent 0 28%,
                     #000 28.5% 96%,
                     transparent 96.5% 100%);
          mask: radial-gradient(circle at center,
                     transparent 0 28%,
                     #000 28.5% 96%,
                     transparent 96.5% 100%);
  padding-top: 0; /* keep wedges seated evenly */
}
.radial-wrapper.gate-mode::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background:
    linear-gradient(#ffffff77, #ffffff77) 50% 0/2px 100% no-repeat,
    linear-gradient(#ffffff77, #ffffff77) 0 50%/100% 2px no-repeat;
  -webkit-mask: radial-gradient(circle at center,
                     transparent 0 28%,
                     #000 28.5% 96%,
                     transparent 96.5% 100%);
          mask: radial-gradient(circle at center,
                     transparent 0 28%,
                     #000 28.5% 96%,
                     transparent 96.5% 100%);
  pointer-events: none;
  z-index: 6; /* above wedges so the lines are always visible */
}
.radial-wrapper.gate-mode .center-btn { inset: 35%; }
.radial-wrapper.gate-mode .seg1.active { transform: translate(12px, -9px) scale(1.08); }
.radial-wrapper.gate-mode .seg2.active { transform: translate(12px, 9px) scale(1.08); }
.radial-wrapper.gate-mode .seg3.active { transform: translate(-12px, 9px) scale(1.08); }
.radial-wrapper.gate-mode .seg4.active { transform: translate(-9px, -12px) scale(1.08); }
/* Tuck non-active wedges slightly inward when engaged to emphasize the active wedge */
.radial-wrapper.gate-mode.gate-engaged .segment:not(.active) { transform: scale(0.96); filter: brightness(0.9); }

/* Claim pills styling */
.claim-pills { display: flex; flex-direction: column; align-items: center; gap: 8px; }
.claim-pills .pill {
  display: inline-flex;
  align-items: center;
  padding: 8px 14px;
  border-radius: 999px;
  background: rgba(10,37,66,.12);
  color: #eaf2ff;
  font-weight: 700;
  box-shadow: 0 2px 8px rgba(0,0,0,.25);
}
.claim-pills .pill .label {
  opacity: 1;
  margin-right: 10px;
  color: #ffffff;
  text-transform: uppercase;
  letter-spacing: .6px;
  font-weight: 800;
  padding: 2px 10px;
  border-radius: 999px;
  background: rgba(255,255,255,.12);
}
.claim-pills .pill .value { font-variant-numeric: tabular-nums; letter-spacing: .3px; color: #000 !important; font-weight: 800; }
.claim-pills .pill-first { border: 1px solid rgba(39,174,96,.7); }
.claim-pills .pill-first .label { background: rgba(46,204,113,.18); box-shadow: inset 0 0 0 1px rgba(46,204,113,.7); color: #2ecc71; }
.claim-pills .pill-first .value { color: #000 !important; }
.claim-pills .pill-last { border: 1px solid rgba(231,76,60,.7); }

/* Large screens - restore spacing and sizing */
@media (min-width: 1601px) {
  .container {
    max-width: 1400px;
  }
  .operator-view .hero {
    padding: 1.5rem 2rem !important;
    margin-bottom: 1.5rem;
  }
  .operator-view .hero h2 {
    font-size: 2rem;
    margin-bottom: 0.5rem;
  }
  .operator-view .hero p {
    font-size: 1rem;
  }
  .col-md-6.mb-2 {
    margin-bottom: 1.5rem !important;
  }
  .card-header {
    padding: 0.75rem 1.25rem;
    font-size: 1rem;
  }
  .card-header .mdi {
    font-size: 20px;
  }
  .card-body {
    padding: 1rem 1.25rem;
  }
  .form-label {
    font-size: 0.95rem;
    margin-bottom: 0.5rem;
  }
  .fancy-select {
    padding: 0.5rem 0.75rem;
    font-size: 0.95rem;
  }
  .radial-wrapper {
    --size: 420px;
  }
  .seg-text { font-size: 18px; }
  .center-btn { font-size: 22px; }
  .center-btn .mdi { font-size: 32px; }
  .results-scroll { max-height: 50vh; }
  .pretty-table {
    font-size: 13px;
  }
  .pretty-table td, .pretty-table th {
    padding: 0.75rem 0.6rem;
  }
  .mt-2.animate-in {
    margin-top: 1.5rem !important;
  }
  .radial-caption {
    font-size: 14px;
  }
  .claim-pills .pill {
    padding: 8px 14px;
    font-size: 13px;
  }
}

/* Responsive breakpoints for Operator page */
@media (max-width: 1600px) {
  .container {
    max-width: 100%;
    padding-left: 1rem;
    padding-right: 1rem;
  }
  .operator-view .hero {
    padding: 1rem 1.5rem !important;
  }
  .operator-view .hero h2 {
    font-size: 1.75rem;
  }
  .radial-wrapper {
    --size: 320px;
  }
  .seg-text { font-size: 14px; }
  .center-btn { font-size: 18px; }
  .center-btn .mdi { font-size: 28px; }
  .results-scroll { max-height: 35vh; }
  .pretty-table {
    font-size: 11px;
  }
  .pretty-table td, .pretty-table th {
    padding: 0.5rem 0.4rem;
  }
  .fancy-select {
    font-size: 13px;
    padding: 0.4rem 0.6rem;
  }
  .card-header {
    padding: 0.75rem 1rem;
    font-size: 14px;
  }
  .badge {
    font-size: 11px;
    padding: 0.25rem 0.5rem;
  }
  .radial-caption {
    font-size: 12px;
  }
  .claim-pills .pill {
    padding: 6px 10px;
    font-size: 11px;
  }
}

@media (max-width: 1400px) {
  .radial-wrapper {
    --size: 280px;
  }
  .seg-text { font-size: 13px; }
  .center-btn { font-size: 16px; }
  .center-btn .mdi { font-size: 26px; }
  .results-scroll { max-height: 32vh; }
  .pretty-table {
    font-size: 10px;
  }
  .pretty-table td, .pretty-table th {
    padding: 0.4rem 0.3rem;
  }
  .fancy-select {
    font-size: 12px;
    padding: 0.35rem 0.5rem;
  }
  .operator-view .hero {
    padding: 0.75rem 1rem !important;
  }
  .operator-view .hero h2 {
    font-size: 1.5rem;
  }
  .operator-view .hero p {
    font-size: 0.875rem;
  }
}

@media (max-width: 1200px) {
  .operator-grid {
    flex-direction: column;
  }
  .operator-left-col,
  .operator-right-col {
    padding-left: 15px;
    padding-right: 15px;
    max-width: 100%;
  }
  .radial-footer {
    position: relative;
    top: 0;
    margin-top: 1rem;
  }
  .radial-wrapper {
    --size: 260px;
  }
  .seg-text { font-size: 12px; }
  .center-btn { font-size: 15px; }
  .center-btn .mdi { font-size: 24px; }
  .pretty-table {
    font-size: 9.5px;
  }
  .pretty-table td, .pretty-table th {
    padding: 0.35rem 0.25rem;
  }
  .results-scroll { max-height: 30vh; }
  .card-header {
    padding: 0.6rem 0.8rem;
    font-size: 13px;
  }
  .badge {
    font-size: 10px;
    padding: 0.2rem 0.4rem;
  }
}

@media (max-width: 992px) {
  .operator-view .hero {
    padding: 0.75rem 1rem !important;
  }
  .operator-view .hero h2 {
    font-size: 1.35rem;
  }
  .operator-view .hero p {
    font-size: 0.8rem;
  }
  .radial-wrapper {
    --size: 240px;
  }
  .seg-text { font-size: 11px; }
  .center-btn { font-size: 14px; }
  .center-btn .mdi { font-size: 22px; }
  .results-scroll { max-height: 45vh; }
  .pretty-table {
    font-size: 9px;
  }
  .pretty-table td, .pretty-table th {
    padding: 0.3rem 0.2rem;
  }
  .fancy-select {
    font-size: 11px;
    padding: 0.3rem 0.4rem;
  }
  .card-header {
    padding: 0.5rem 0.75rem;
    font-size: 12px;
  }
  .radial-caption {
    font-size: 11px;
  }
  .claim-pills .pill {
    padding: 5px 8px;
    font-size: 10px;
  }
}

@media (max-width: 768px) {
  .container {
    padding-left: 1rem;
    padding-right: 1rem;
  }
  .operator-view .hero h2 {
    font-size: 1.5rem;
  }
  .radial-wrapper {
    --size: 280px;
  }
  .seg-text { font-size: 14px; }
  .center-btn { font-size: 18px; }
  .center-btn .mdi { font-size: 28px; }
  .results-scroll {
    max-height: 50vh;
    overflow-x: auto;
  }
  .pretty-table {
    font-size: 10px;
    min-width: 800px;
  }
  .pretty-table td, .pretty-table th {
    padding: 0.5rem;
  }
  .card-header {
    flex-direction: column;
    align-items: flex-start !important;
    gap: 0.5rem;
  }
  .fancy-select {
    font-size: 14px;
    padding: 0.5rem;
  }
}

@media (max-width: 576px) {
  .operator-view .hero {
    padding: 1rem !important;
  }
  .operator-view .hero h2 {
    font-size: 1.25rem;
  }
  .operator-view .hero p {
    font-size: 0.875rem;
  }
  .radial-wrapper {
    --size: 240px;
  }
  .seg-text { font-size: 12px; }
  .center-btn { font-size: 16px; }
  .center-btn .mdi { font-size: 24px; }
  .radial-caption { font-size: 12px; }
  .claim-pills .pill {
    padding: 6px 10px;
    font-size: 11px;
  }
  .results-scroll {
    max-height: 45vh;
  }
  .pretty-table {
    font-size: 9px;
    min-width: 700px;
  }
  .card-header strong {
    font-size: 14px;
  }
  .badge {
    font-size: 10px;
  }
}

.center-btn:not(.disabled):hover .mdi-lock-outline {
  transform: scale(1.1);
}
.center-btn.unlocking .mdi-lock-outline {
  animation: unlock-to-open 0.6s ease forwards;
}
@keyframes unlock-to-open {
  0% { transform: rotate(0deg) scale(1); opacity: 1; }
  30% { transform: rotate(-20deg) scale(1.2); opacity: 1; }
  50% { transform: rotate(10deg) scale(1.15); opacity: 0.7; }
  70% { transform: rotate(-5deg) scale(1.1); opacity: 0.3; }
  100% { transform: rotate(0deg) scale(1.3); opacity: 0; }
}
</style>
<style>
/* Operator table sorting visuals */
.pretty-table th.sortable { cursor: pointer; user-select: none; white-space: nowrap; }
.pretty-table th .sort-icon { margin-left: 6px; font-size: 16px; vertical-align: middle; opacity: .6; }
.pretty-table th.active .sort-icon { opacity: 1; color: #007bff; }
</style>
